<template>
  <div class="py-4">
    <!-- Selection Banner -->
    <v-slide-y-transition>
      <div
        v-if="totalSelectedCount > 0"
        class="tree-selection-banner d-flex align-center justify-space-between mb-3">
        <span class="text-caption selection-count">
          <strong>{{ totalSelectedCount }}</strong> image{{ totalSelectedCount > 1 ? 's' : '' }} selected
        </span>
        <div class="d-flex align-center" style="gap: 8px;">
          <v-btn
            x-small
            text
            @click="clearAllSelections">
            <v-icon x-small class="mr-1">mdi-close</v-icon>
            Discard
          </v-btn>
          <v-btn
            v-has="permissions.Admin360Images.Delete"
            x-small
            text
            color="error"
            @click="deleteSelectedImages">
            <v-icon x-small class="mr-1">mdi-delete-outline</v-icon>
            Delete
          </v-btn>
        </div>
      </div>
    </v-slide-y-transition>

    <!-- Loading State -->
    <div v-if="loading" class="d-flex justify-center align-center pa-8">
      <AvaLoader type="pulse" size="medium" text="Loading Projects data..." />
    </div>

    <!-- Empty State -->
    <div v-else-if="!items || items.length === 0" class="text-center pa-8">
      <v-icon size="48" color="grey lighten-1">mdi-folder-open-outline</v-icon>
      <p class="mt-3 grey--text">No projects found</p>
    </div>

    <!-- Tree View -->
    <template v-else>
      <!-- Tree Controls -->
      <div class="d-flex align-center mb-2" style="gap: 8px;">
        <v-btn x-small outlined color="primary" @click="expandAll">
          <v-icon x-small class="mr-1">mdi-unfold-more-horizontal</v-icon>
          Expand All
        </v-btn>
        <v-btn x-small outlined color="primary" @click="collapseAll">
          <v-icon x-small class="mr-1">mdi-unfold-less-horizontal</v-icon>
          Collapse All
        </v-btn>
      </div>

      <v-treeview
        :items="items"
        :search="search"
        :open.sync="openNodes"
        item-key="id"
        activatable
        open-on-click
        class="admin-projects-tree">
      <template #prepend="{ item }">
        <!-- Project node -->
        <v-icon v-if="item.type === 'project'" color="grey" small>
          {{ item.hasImages ? 'mdi-folder-image' : 'mdi-folder' }}
        </v-icon>
        <!-- Image node -->
        <div v-else-if="item.type === 'image'" class="d-flex align-center">
          <v-checkbox
            :input-value="isImageSelected(item)"
            @click.stop
            @change="toggleImageSelection(item)"
            hide-details
            dense
            class="mt-0 pt-0 mr-2 tree-image-checkbox"
          ></v-checkbox>
          <div class="tree-image-thumbnail">
            <v-img
              :src="item.image.thumbnailURL"
              :alt="item.name"
              width="40"
              height="40"
              class="rounded">
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-icon small>mdi-image</v-icon>
                </v-row>
              </template>
            </v-img>
            <v-icon
              v-if="item.hasGPS"
              x-small
              class="gps-badge-tree"
              color="success">
              mdi-crosshairs-gps
            </v-icon>
          </div>
        </div>
      </template>

      <template #label="{ item }">
        <div class="d-flex align-center justify-space-between" style="width: 100%;">
          <div class="d-flex align-center" style="gap: 8px;">
            <span>{{ item.name }}</span>
            <!-- Image count or "No images" for leaf projects -->
            <v-chip
              v-if="item.type === 'project' && item.hasImages && item.imageCount > 0"
              x-small
              color="primary">
              {{ item.imageCount }} images
            </v-chip>
            <span
              v-else-if="item.type === 'project' && item.hasImages && item.imageCount === 0"
              class="no-images-text">
              No images
            </span>
            <!-- Insight badge for images -->
            <v-chip
              v-if="item.type === 'image' && item.hasInsights"
              x-small
              color="warning">
              {{ item.insightsCount }} Inspects
            </v-chip>
            <!-- Archived badge -->
            <v-chip
              v-if="item.isArchived"
              x-small
              color="error"
              dark>
              <v-icon x-small class="mr-1">mdi-archive</v-icon>
              Archived
            </v-chip>
          </div>

          <!-- Project actions menu -->
          <div v-if="item.type === 'project'" class="d-flex" style="gap: 4px;">
            <v-progress-circular
              v-if="item.imageCount > 0 && (processingAnalysisJob || postProcessLoadingIds.includes(item.id))"
              indeterminate
              size="16"
              width="2"
              color="primary"
              class="mr-1"></v-progress-circular>
            <v-menu offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  x-small
                  v-bind="attrs"
                  v-on="on"
                  @click.stop>
                  <v-icon small>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <!-- Selection actions (only when project has images) -->
                <template v-if="item.hasImages && item.imageCount > 0">
                  <v-list-item @click="selectAllProjectImages(item)">
                    <v-list-item-icon>
                      <v-icon small>mdi-checkbox-multiple-marked</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Select All</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    @click="deselectAllProjectImages(item)"
                    :disabled="!hasProjectSelections(item)">
                    <v-list-item-icon>
                      <v-icon small>mdi-checkbox-multiple-blank-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Deselect All</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider></v-divider>
                </template>

                <!-- Add Folder (disabled once the folder holds images) -->
                <v-list-item
                  v-has="permissions.Admin360Images.Projects"
                  @click="$emit('add-folder', item)"
                  :disabled="!canAddFolder(item)">
                  <v-list-item-icon>
                    <v-icon small>mdi-folder-plus</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Add Folder</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <!-- Upload Images 360 (available while the folder has no sub-folders) -->
                <v-list-item
                  v-if="canUploadImages(item)"
                  v-has="permissions.Admin360Images.Upload"
                  @click="$emit('upload', item)">
                  <v-list-item-icon>
                    <v-icon small>mdi-upload</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Upload Images 360</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <v-divider></v-divider>

                <!-- Run AI (only when images exist) -->
                <v-list-item
                  v-if="item.imageCount > 0"
                  v-has="permissions.AI.Access"
                  @click="$emit('run-ai', item)"
                  :disabled="processingAnalysisJob">
                  <v-list-item-icon>
                    <v-icon small color="success">mdi-play</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Run AI</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <!-- Post Process (only when images exist) -->
                <v-list-item
                  v-if="item.imageCount > 0"
                  v-has="permissions.AI.Access"
                  @click="$emit('post-process', item)"
                  :disabled="!postProcessAvailable(item) || postProcessLoadingIds.includes(item.id)">
                  <v-list-item-icon>
                    <v-icon small color="success">mdi-wrench-cog</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Post Process</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider v-if="item.imageCount > 0"></v-divider>

                <!-- Archive toggle -->
                <v-list-item
                  @click="$emit('toggle-archive', item)"
                  :disabled="!$has(permissions.Admin360Images.Projects)">
                  <v-list-item-icon>
                    <v-icon small>{{ item.isArchived ? 'mdi-archive-arrow-up' : 'mdi-archive-arrow-down' }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.isArchived ? 'Unarchive' : 'Archive' }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <!-- Move -->
                <v-list-item
                  v-has="permissions.Admin360Images.Projects"
                  @click="$emit('move', item)">
                  <v-list-item-icon>
                    <v-icon small>mdi-folder-move</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Move</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>

                <!-- Edit -->
                <v-list-item
                  @click="$emit('edit', item)"
                  :disabled="!$has(permissions.Admin360Images.Projects)">
                  <v-list-item-icon>
                    <v-icon small>mdi-pencil</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <!-- Delete -->
                <v-list-item
                  @click="$emit('delete', item)"
                  :disabled="!$has(permissions.Admin360Images.Projects)">
                  <v-list-item-icon>
                    <v-icon small color="error">mdi-delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title class="error--text">Delete</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>

          <!-- Image delete button (visible on hover) -->
          <div v-else-if="item.type === 'image'" class="image-hover-actions">
            <v-btn
              v-has="permissions.Admin360Images.Delete"
              icon
              x-small
              color="error"
              @click.stop="$emit('delete-image', findParentProject(item), item.image)">
              <v-icon small>mdi-delete-outline</v-icon>
            </v-btn>
          </div>
        </div>
      </template>
    </v-treeview>
    </template>
  </div>
</template>

<script>
import { $has } from '@/utils';
import AvaLoader from '@/components/loaders/AvaLoader.vue';

export default {
  name: 'ProjectsTreeView',

  components: {
    AvaLoader,
  },

  props: {
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    search: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    processingAnalysisJob: {
      type: Boolean,
      default: false
    },
    postProcessLoadingIds: {
      type: Array,
      default: () => []
    },
    permissions: {
      type: Object,
      required: true
    },
    pointClouds: {
      type: Array,
      default: () => []
    },
    selectedImages: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      openNodes: [],
    };
  },

  computed: {
    allExpandableIds() {
      const ids = [];
      const collect = (nodes) => {
        for (const node of nodes) {
          if (node.children && node.children.length > 0) {
            ids.push(node.id);
            collect(node.children);
          }
        }
      };
      collect(this.items);
      return ids;
    },

    totalSelectedCount() {
      let count = 0;
      Object.keys(this.selectedImages).forEach(projectId => {
        if (this.selectedImages[projectId]) {
          count += this.selectedImages[projectId].length;
        }
      });
      return count;
    }
  },

  methods: {
    $has,

    expandAll() {
      this.openNodes = [...this.allExpandableIds];
    },

    collapseAll() {
      this.openNodes = [];
    },

    /** True when the folder directly holds 360 images. */
    holdsImages(item) {
      if (item.hasImages) return true;
      return !!item.children && item.children.some(c => c.type !== 'project');
    },

    /** True when the folder contains sub-folders. */
    holdsFolders(item) {
      return !!item.children && item.children.some(c => c.type === 'project');
    },

    /**
     * A folder can receive sub-folders as long as it does not already hold
     * images. An empty folder qualifies — it is committed to neither yet.
     */
    canAddFolder(item) {
      return !this.holdsImages(item);
    },

    /**
     * A folder can receive image uploads as long as it has no sub-folders.
     * An empty folder qualifies — uploading is how it becomes a leaf.
     */
    canUploadImages(item) {
      return !this.holdsFolders(item);
    },

    postProcessAvailable(project) {
      if (!project || !project.name) return false;
      const matchedPc = this.pointClouds.find((o) => o.name === project.name);
      return matchedPc && project.images && project.images.some((o) => o.anomalyInsights?.length > 0);
    },

    findParentProject(imageItem) {
      if (imageItem.project) return imageItem.project;

      const search = (nodes) => {
        for (const node of nodes) {
          if (node.type === 'project' && node.hasImages) {
            if (node.children && node.children.some(c => c.id === imageItem.id)) {
              return node;
            }
          }
          if (node.children) {
            const result = search(node.children);
            if (result) return result;
          }
        }
        return null;
      };
      return search(this.items);
    },

    isImageSelected(imageItem) {
      const project = this.findParentProject(imageItem);
      if (!project) return false;
      const projectSelected = this.selectedImages[project.id] || [];
      return projectSelected.some(img => img.id === imageItem.image.id);
    },

    toggleImageSelection(imageItem) {
      const project = this.findParentProject(imageItem);
      if (!project) return;

      const projectId = project.id;
      const image = imageItem.image;
      const currentSelection = this.selectedImages[projectId] || [];
      const isCurrentlySelected = currentSelection.some(img => img.id === image.id);

      let newSelection;
      if (isCurrentlySelected) {
        newSelection = currentSelection.filter(img => img.id !== image.id);
      } else {
        newSelection = [...currentSelection, image];
      }

      this.$emit('update:selected-images', {
        ...this.selectedImages,
        [projectId]: newSelection
      });
    },

    clearAllSelections() {
      this.$emit('clear-selection');
    },

    deleteSelectedImages() {
      this.$emit('delete-selected');
    },

    collectLeafImages(node) {
      const result = [];
      if (node.type === 'image') {
        result.push(node);
      } else if (node.children) {
        node.children.forEach(child => {
          result.push(...this.collectLeafImages(child));
        });
      }
      return result;
    },

    selectAllProjectImages(projectItem) {
      const leafImages = this.collectLeafImages(projectItem);
      if (leafImages.length === 0) return;

      const projectId = projectItem.id;
      const currentSelection = this.selectedImages[projectId] || [];
      const images = leafImages.map(img => img.image);
      const newImages = images.filter(
        img => !currentSelection.some(sel => sel.id === img.id)
      );

      this.$emit('update:selected-images', {
        ...this.selectedImages,
        [projectId]: [...currentSelection, ...newImages]
      });
    },

    deselectAllProjectImages(projectItem) {
      const leafImages = this.collectLeafImages(projectItem);
      if (leafImages.length === 0) return;

      const projectId = projectItem.id;
      const currentSelection = this.selectedImages[projectId] || [];
      const imageIds = leafImages.map(img => img.image.id);

      this.$emit('update:selected-images', {
        ...this.selectedImages,
        [projectId]: currentSelection.filter(img => !imageIds.includes(img.id))
      });
    },

    hasProjectSelections(projectItem) {
      const projectSelected = this.selectedImages[projectItem.id] || [];
      return projectSelected.length > 0;
    },
  }
};
</script>

<style scoped lang="scss">
.admin-projects-tree {
  background: rgba(30, 35, 40, 0.5);
  border-radius: 4px;
  padding: 16px;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }

  ::v-deep .v-treeview-node__root {
    min-height: 48px;
  }

  ::v-deep .v-treeview-node__label {
    font-size: 14px;
    flex-grow: 1;
  }

  ::v-deep .v-treeview-node__level {
    width: 24px;
  }

  .theme--light & {
    background: rgba(255, 255, 255, 0.8);

    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.05);
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(0, 0, 0, 0.2);

      &:hover {
        background: rgba(0, 0, 0, 0.3);
      }
    }
  }
}

.tree-image-thumbnail {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 40px;

  .gps-badge-tree {
    position: absolute;
    bottom: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 50%;
    padding: 2px;
  }
}

.tree-selection-banner {
  padding: 8px 16px;
  border-left: 3px solid var(--v-primary-base);
  background: rgba(30, 35, 40, 0.7);
  border-radius: 4px;

  .selection-count {
    opacity: 0.8;

    strong {
      opacity: 1;
      font-weight: 600;
      color: var(--v-primary-base);
    }
  }

  .theme--light & {
    background: rgba(255, 255, 255, 0.9);
    border-left-color: var(--v-primary-base);
  }
}

.tree-image-checkbox {
  ::v-deep .v-input--selection-controls__input {
    margin-right: 0;
  }

  ::v-deep .v-icon {
    font-size: 18px;
  }
}

.no-images-text {
  font-style: italic;
  opacity: 0.5;
  font-size: 12px;
}

.image-hover-actions {
  opacity: 0;
  transition: opacity 0.15s ease;
}

::v-deep .v-treeview-node__root:hover .image-hover-actions {
  opacity: 1;
}
</style>
